import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scale, MapPin, Clock, TrendingUp } from "lucide-react";

const PrixTransportColisMaroc = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Prix Transport Colis France Maroc – Tarifs et Estimation | GoBledPro</title>
        <meta 
          name="description" 
          content="Découvrez les prix du transport de colis entre la France et le Maroc. Fourchettes tarifaires, facteurs de variation et comparatif des solutions. Obtenez une estimation personnalisée." 
        />
        <link rel="canonical" href="https://gobledpro.com/prix-transport-colis-maroc" />
        <meta property="og:title" content="Prix Transport Colis France Maroc – Tarifs et Estimation | GoBledPro" />
        <meta property="og:description" content="Découvrez les prix du transport de colis entre la France et le Maroc. Fourchettes tarifaires et comparatif des solutions." />
        <meta property="og:image" content="https://gobledpro.com/og-prix-transport.png" />
        <meta property="og:url" content="https://gobledpro.com/prix-transport-colis-maroc" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GoBledPro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gobledpro.com/og-prix-transport.png" />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <header className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
                Guide Tarifs
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Prix du Transport de Colis France ⇄ Maroc
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Comprendre les tarifs, les facteurs de variation et obtenir une estimation adaptée à votre envoi.
              </p>
            </header>

            {/* Introduction */}
            <section className="prose prose-lg max-w-none mb-12">
              <p className="text-muted-foreground leading-relaxed">
                Le coût d'un envoi de colis entre la France et le Maroc varie en fonction de nombreux critères. 
                Cette page vous aide à comprendre les différentes composantes du prix et à identifier 
                la solution la plus adaptée à vos besoins et à votre budget.
              </p>
            </section>

            {/* Fourchettes de prix */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Fourchettes de prix indicatives
              </h2>
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  Les tarifs ci-dessous sont donnés à titre indicatif et peuvent varier selon les transporteurs, 
                  la période et les conditions spécifiques de votre envoi.
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-background rounded-xl p-5 border border-border">
                    <h3 className="font-semibold text-foreground mb-2">Petit colis</h3>
                    <p className="text-sm text-muted-foreground mb-3">Jusqu'à 5 kg</p>
                    <p className="text-2xl font-bold text-primary">15 € – 40 €</p>
                  </div>
                  <div className="bg-background rounded-xl p-5 border border-border">
                    <h3 className="font-semibold text-foreground mb-2">Colis moyen</h3>
                    <p className="text-sm text-muted-foreground mb-3">5 à 20 kg</p>
                    <p className="text-2xl font-bold text-primary">40 € – 80 €</p>
                  </div>
                  <div className="bg-background rounded-xl p-5 border border-border">
                    <h3 className="font-semibold text-foreground mb-2">Gros colis</h3>
                    <p className="text-sm text-muted-foreground mb-3">20 à 50 kg</p>
                    <p className="text-2xl font-bold text-primary">80 € – 150 €</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-6 italic">
                  * Ces fourchettes sont indicatives. Le prix final dépend de multiples facteurs détaillés ci-dessous.
                </p>
              </div>
            </section>

            {/* Facteurs de variation */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Ce qui fait varier le prix
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Scale className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Poids et volume</h3>
                  <p className="text-muted-foreground text-sm">
                    Le poids réel ou le poids volumétrique (le plus élevé des deux) détermine une grande partie du tarif. 
                    Un colis léger mais volumineux peut coûter plus cher qu'un colis compact et lourd.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Villes de départ et d'arrivée</h3>
                  <p className="text-muted-foreground text-sm">
                    Un envoi Paris → Casablanca coûte généralement moins cher qu'un envoi depuis une ville isolée 
                    vers une zone rurale au Maroc. La distance et l'accessibilité jouent un rôle.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Délai souhaité</h3>
                  <p className="text-muted-foreground text-sm">
                    Un envoi express (3-5 jours) coûte plus cher qu'un transport standard (7-14 jours). 
                    La flexibilité sur le délai permet souvent de réduire le prix.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Période de l'année</h3>
                  <p className="text-muted-foreground text-sm">
                    Les vacances d'été, Ramadan et fêtes de fin d'année sont des périodes de forte demande. 
                    Les tarifs peuvent augmenter de 20 à 30 % pendant ces pics.
                  </p>
                </div>
              </div>
            </section>

            {/* Comparatif */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Comparatif des solutions de transport
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-card border border-border rounded-xl overflow-hidden">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-semibold text-foreground">Solution</th>
                      <th className="text-left p-4 font-semibold text-foreground">Prix indicatif</th>
                      <th className="text-left p-4 font-semibold text-foreground">Délai</th>
                      <th className="text-left p-4 font-semibold text-foreground">Avantages</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="p-4 font-medium text-foreground">DHL / FedEx</td>
                      <td className="p-4 text-muted-foreground">60 € – 150 €</td>
                      <td className="p-4 text-muted-foreground">3-5 jours</td>
                      <td className="p-4 text-muted-foreground text-sm">Rapidité, suivi précis</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium text-foreground">La Poste / Colissimo</td>
                      <td className="p-4 text-muted-foreground">25 € – 80 €</td>
                      <td className="p-4 text-muted-foreground">7-15 jours</td>
                      <td className="p-4 text-muted-foreground text-sm">Accessible, réseau dense</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium text-foreground">Transport via particulier</td>
                      <td className="p-4 text-muted-foreground">15 € – 50 €</td>
                      <td className="p-4 text-muted-foreground">Variable</td>
                      <td className="p-4 text-muted-foreground text-sm">Prix bas, mais risques</td>
                    </tr>
                    <tr className="bg-primary/5">
                      <td className="p-4 font-medium text-primary">GoBledPro (plateforme)</td>
                      <td className="p-4 text-foreground">20 € – 100 €</td>
                      <td className="p-4 text-foreground">5-12 jours</td>
                      <td className="p-4 text-foreground text-sm">Transporteurs vérifiés, prix compétitifs, suivi</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                GoBledPro met en relation les clients avec des transporteurs indépendants professionnels, 
                permettant d'obtenir des tarifs compétitifs tout en bénéficiant d'un cadre sécurisé.
              </p>
            </section>

            {/* Pourquoi les prix varient */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Pourquoi les prix varient-ils autant ?
              </h2>
              <div className="bg-muted/30 rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-4">
                  Contrairement aux grandes enseignes qui appliquent des grilles tarifaires fixes, 
                  les transporteurs indépendants adaptent leurs prix en fonction de :
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Leur planning et capacité de chargement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Le regroupement possible avec d'autres envois</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">La distance réelle entre les points de collecte et de livraison</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Les coûts de carburant et de péage actuels</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  C'est pourquoi la meilleure approche reste de demander une estimation personnalisée 
                  en fonction de votre situation précise.
                </p>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Obtenez un prix exact pour votre envoi
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Décrivez votre colis et recevez des propositions de transporteurs vérifiés, 
                avec des prix adaptés à votre trajet et vos délais.
              </p>
              <Button asChild size="lg" className="gap-2">
                <Link to="/demande-transport">
                  Obtenir un prix exact
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </section>

            {/* Liens utiles */}
            <div className="mt-12 pt-8 border-t border-border flex flex-wrap justify-between items-center gap-4">
              <Link 
                to="/blog" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Retour au blog
              </Link>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/transporteurs-verifies-maroc" className="text-primary hover:underline">Transporteurs vérifiés →</Link>
                <Link to="/faq-transport-colis-maroc" className="text-primary hover:underline">FAQ →</Link>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrixTransportColisMaroc;
